/**
 * Consumer Data Collection Form
 * Optional form for consumers to provide feedback and contact information
 */

import React, { useState } from 'react';
import { Button, Input } from './ui';

interface ConsumerData {
  email?: string;
  phone?: string;
  name?: string;
  purchaseLocation?: string;
  rating?: number;
  feedback?: string;
}

interface ConsumerDataFormProps {
  onSubmit: (data: ConsumerData) => void;
  onSkip: () => void;
  loading?: boolean;
}

const ConsumerDataForm: React.FC<ConsumerDataFormProps> = ({ onSubmit, onSkip, loading }) => {
  const [formData, setFormData] = useState<ConsumerData>({});
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);

  const handleChange = (field: keyof ConsumerData, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const hasAnyData = Object.values(formData).some((value) => value !== undefined && value !== '');

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Help Us Improve! 🎯
        </h3>
        <p className="text-sm text-gray-600">
          Share your experience with this product. All fields are optional and your data is kept private.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            How would you rate this product?
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleChange('rating', star)}
                onMouseEnter={() => setHoveredStar(star)}
                onMouseLeave={() => setHoveredStar(null)}
                className="text-3xl transition-transform hover:scale-110 focus:outline-none"
              >
                <span
                  className={
                    (hoveredStar !== null ? star <= hoveredStar : star <= (formData.rating || 0))
                      ? 'text-yellow-400'
                      : 'text-gray-300'
                  }
                >
                  ⭐
                </span>
              </button>
            ))}
          </div>
          {formData.rating && (
            <p className="text-sm text-gray-600 mt-1">
              {formData.rating === 5 && 'Excellent! 🎉'}
              {formData.rating === 4 && 'Great! 👍'}
              {formData.rating === 3 && 'Good 👌'}
              {formData.rating === 2 && 'Fair 😐'}
              {formData.rating === 1 && 'Poor 😞'}
            </p>
          )}
        </div>

        {/* Feedback */}
        <div>
          <label htmlFor="feedback" className="block text-sm font-medium text-gray-700 mb-1">
            Your Feedback (Optional)
          </label>
          <textarea
            id="feedback"
            rows={3}
            value={formData.feedback || ''}
            onChange={(e) => handleChange('feedback', e.target.value)}
            placeholder="Tell us about your experience..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-gray-900 bg-white placeholder-gray-500"
            style={{ color: '#111827' }}
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Your Name (Optional)
          </label>
          <Input
            id="name"
            type="text"
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="John Doe"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email (Optional)
          </label>
          <Input
            id="email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="your@email.com"
          />
          <p className="text-xs text-gray-500 mt-1">
            We'll only use this to contact you about warranty or product updates
          </p>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number (Optional)
          </label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="+91-9876543210"
          />
        </div>

        {/* Purchase Location */}
        <div>
          <label htmlFor="purchaseLocation" className="block text-sm font-medium text-gray-700 mb-1">
            Where did you buy this? (Optional)
          </label>
          <Input
            id="purchaseLocation"
            type="text"
            value={formData.purchaseLocation || ''}
            onChange={(e) => handleChange('purchaseLocation', e.target.value)}
            placeholder="Store name or location"
          />
        </div>

        {/* Privacy Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex gap-2">
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <p className="text-xs text-blue-900 font-medium">Your Privacy Matters</p>
              <p className="text-xs text-blue-700 mt-1">
                Your data is encrypted and stored securely. We never share your personal information with third parties.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={onSkip}
            disabled={loading}
          >
            Skip
          </Button>
          <Button
            type="submit"
            fullWidth
            disabled={loading || !hasAnyData}
          >
            {loading ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </form>

      {/* Benefits */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-xs font-medium text-gray-700 mb-2">Why share your information?</p>
        <ul className="space-y-1 text-xs text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-green-600 mt-0.5">✓</span>
            <span>Get warranty support and product updates</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600 mt-0.5">✓</span>
            <span>Help improve product quality</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600 mt-0.5">✓</span>
            <span>Receive exclusive offers and discounts</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ConsumerDataForm;
